import React from "react";
import SectionHeading from "components/SectionHeading";
import classes from "../style.module.scss";

//TODO: answer question about what we do with anonymized upgrade measure costs

export default () => (
    <div>
        <SectionHeading title="FAQs" subtitle="Answers to frequently asked questions." />
        <div className={classes.contentWrapper}>
            <div className={classes.faqSection}>
                <h2>General Volta SNAP Questions</h2>

                <div className={classes.faq}>
                    <h3>Which portions of Volta SNAP are free to use and which are paid?</h3>
                    <p>Paid features in Volta SNAP include:</p>
                    <ul>
                        <li>Parametric and Community Analysis tools, requiring 1 credit per 2,500 runs</li>
                        <li>
                            Insights features, requiring 1 credit per activated model, which include:
                            <ul>
                                <li>
                                    Room-by-room F280 calculations and report generation (Free until September 2026)
                                </li>
                                <li>EnergyPlus simulations (Free until April 2027)</li>
                                <li>Utility Analysis (Free until April 2027)</li>
                                <li>More to come…</li>
                            </ul>
                        </li>
                    </ul>
                    <p>
                        All other features, including whole-home CSA F280-12 heat loss/heat gain calculations and report
                        generation, are free to use.
                    </p>
                </div>

                <div className={classes.faq}>
                    <h3>How can I run simulations in EnerGuide or Ontario Reference House modes?</h3>
                    <p>
                        To run simulations in EnerGuide or Ontario Reference House modes, users must be Registered
                        Energy Advisors (REA) in good standing with NRCan. All non-REA users have access to General mode
                        simulations, which includes CSA F280-12 results. If you believe you should have access to these
                        REA simulation modes, please contact support.
                    </p>
                </div>

                <div className={classes.faq}>
                    <h3>My account is unverified. What does that mean, and what do I need to do to verify it?</h3>
                    <p>
                        Volta SNAP is intended for use by industry professionals. To ensure the integrity of the app and
                        the data it produces, we require all users to be verified. Verification involves a review of the
                        user's industry credentials and experience. Example professions include: Registered Energy
                        Advisor (REA), Certified Energy Manager (CEM), LEED Accredited Professional (LEED AP), Engineer,
                        Architect, HVAC Designer, etc.
                    </p>
                    <p>
                        To request verification, please contact info@voltaresearch.org with proof of your industry
                        credentials and experience.
                    </p>
                    <p>
                        Unverified accounts are limited to five models and cannot access Insights, Parametric Analysis,
                        non-General simulations, or F280 reports.
                    </p>
                </div>

                <div className={classes.faq}>
                    <h3>How do I add/manage my organization within Volta SNAP? Can I be part of more than one?</h3>
                    <p>
                        Users can be a member of multiple organizations in Volta SNAP, and can change their active
                        organization by clicking their name in the upper right corner, followed by{" "}
                        <strong>Organization</strong>. Organization administrators can add new users or remove users
                        from their organization from this screen.
                    </p>
                </div>

                <div className={classes.faq}>
                    <h3>How do I report an error or provide feedback?</h3>
                    <p>
                        When Volta SNAP crashes or simulations do not return results, Volta SNAP developers are
                        automatically notified and will deploy a fix as soon as reasonably possible. For all other
                        feedback or issue reporting, please use the megaphone icon in the bottom right corner to flag an
                        issue.
                    </p>
                    <p>
                        Please note that while Volta SNAP staff try to address issues as soon as possible, they are not
                        able to respond to urgent requests. Users should always set aside ample time between modelling
                        activities in Volta SNAP and any associated deadlines.
                    </p>
                </div>

                <div className={classes.faq}>
                    <h3>
                        What are the primary differences between the outputs of a HOT2000 model and a Volta SNAP model?
                    </h3>
                    <p>
                        Volta SNAP uses the HOT2000 simulation engine to produce all of the same outputs as a HOT2000
                        model. In addition to HOT2000-calculated results, Volta SNAP provides a number of additional
                        results metrics, which include:
                    </p>
                    <ul>
                        <li>Additional performance metrics calculated using HOT2000 parameters</li>
                        <li>Hourly electrical load estimates</li>
                        <li>CSA F280-12 heat loss &amp; heat gain outputs</li>
                        <li>EnergyPlus simulation results</li>
                        <li>Utility Analysis results</li>
                        <li>Embodied carbon results</li>
                    </ul>
                </div>

                <div className={classes.faq}>
                    <h3>What anonymized upgrade cost data does Volta SNAP collect, and how is it used?</h3>
                    <p>
                        Volta SNAP may collect upgrade measure cost inputs you enter in the app and process them in
                        anonymized or de-identified form. This information is used to create aggregated statistics that
                        help us improve the Platform and, over time, generate default costing references that other
                        users can leverage.
                    </p>{" "}
                    <p>
                        When this costing data is collected for those purposes, it is not associated with builder or
                        client identity. It may be paired only with limited, non-identifying building context, such as
                        the material or equipment type it is tied to, building type and geometry, the model's HOT2000
                        weather region and location, and the first three digits of a postal code where available.{" "}
                    </p>
                    <p>
                        This does not change your ownership of project content. Collection and use of personal and
                        project data more broadly are described in our Privacy Policy, Terms and Conditions, and
                        Software Licensing Agreement.
                    </p>
                </div>

                <div className={classes.faq}>
                    <h3>How do I purchase and manage credits?</h3>
                    <p>
                        Credits can be purchased from the app header, and organization admins can transfer credits
                        between users. If you have legacy credits from before version 1.0.0, you can still use them to
                        unlock parametric runs or transfer them to the new credit system from the Transactions page.
                    </p>
                    <p>
                        Credits are non-refundable and cannot be retrieved after they have been used to unlock app
                        features (Insights, Parametric Analysis, etc.).
                    </p>
                </div>
            </div>

            <div className={classes.faqSection}>
                <h2>Modelling Questions</h2>

                <div className={classes.faq}>
                    <h3>How do I apply a code to multiple components at once?</h3>
                    <p>
                        <strong>In the Enclosure Tab:</strong>
                    </p>
                    <p>
                        Edit a code or create a new code with the <strong>Type</strong> field of one of the components
                        you want to update. When you're done defining the code details, use the multi-select{" "}
                        <strong>Apply to multiple components</strong> field to choose all the components in your model
                        you want to apply that code to.
                    </p>
                    <p>
                        <strong>In the Upgrades Table:</strong>
                    </p>
                    <p>
                        Use the Components drop-down field to select one or multiple components to apply the upgrade to.
                    </p>
                </div>

                <div className={classes.faq}>
                    <h3>Why don't my results change when I adjust Lighting and Appliances in the upgrade packages?</h3>
                    <p>
                        When working in EnerGuide or Ontario Reference House modes, certain baseloads are fixed
                        according to program standards and cannot be upgraded individually. To reflect a change in these
                        values while simulating with one of these program modes, you must adjust and apply reduced
                        operating conditions in the <strong>Review/Programs</strong> tab, and then select the desired
                        operating condition from the <strong>Operating Conditions</strong> select field in the upgrade
                        table. This is the only way to ensure the data translates correctly to HOT2000.
                    </p>
                    <p>
                        If simulating in General mode, base load upgrades can be applied via the cells in the upgrades
                        table.
                    </p>
                </div>

                <div className={classes.faq}>
                    <h3>
                        Can I export my takeoff measurements, such as perimeters and areas, for my Design Compliance
                        Forms (DCFs) or Quality Assurance (QA)?
                    </h3>
                    <p>
                        Yes, you can export a DCF from the <strong>Review/Export</strong> tab.
                    </p>
                </div>

                <div className={classes.faq}>
                    <h3>
                        Can the Draw Capture tool be used for non-engineered drawings or custom sketches from programs
                        like SketchUp?
                    </h3>
                    <p>
                        Drawings imported into the Drawing Capture Tool must be drawn to scale; otherwise, their
                        measurements won't be accurate.
                    </p>
                </div>

                <div className={classes.faq}>
                    <h3>
                        Are wall heights and header heights automatically defaulted when I trace floor plans in Takeoff
                        Mode?
                    </h3>
                    <p>
                        No, you must identify wall and header heights by annotating an elevation drawing and applying a{" "}
                        <strong>Line</strong> annotation to a <strong>Wall Height</strong> or{" "}
                        <strong>Floor Header Height</strong> field.
                    </p>
                </div>

                <div className={classes.faq}>
                    <h3>What is the required formatting for importing a window spreadsheet into Volta SNAP?</h3>
                    <p>
                        Label, Direction, Width, Height, Overhang Width, Header Height, and Count. All units must match
                        those specified by your unit selection (Metric, Imperial, or US). You can copy and paste window
                        details from a spreadsheet either in Takeoff Data Entry Mode, or in HOT2000 mode via the “(+)
                        Window Table” button in the parent components actions menu (“...”).
                    </p>
                </div>

                <div className={classes.faq}>
                    <h3>
                        When building a code, embodied carbon data is shown with a colour scale. What is this colour
                        scale based on?
                    </h3>
                    <p>
                        The embodied carbon colour scale is based on the industry-average material selections for each
                        layer category. In all cases, the most common material selection for a given category is given
                        an average rating, indicated by the middle yellow-green colour. A colour scale is then applied
                        based on a material's normalized embodied emissions within a given category, in the following
                        manner:
                    </p>
                    <ul>
                        <li>70% lower emissions than average: Green</li>
                        <li>Average: Yellow-green</li>
                        <li>More than 2x greater emissions than average: Yellow</li>
                        <li>More than 4x greater emissions than average: Orange-red</li>
                    </ul>
                    <p>Embodied carbon EPD data was last updated in March 2026.</p>
                </div>
            </div>

            <div className={classes.faqSection}>
                <h2>CSA F280-12 Heat Loss/Heat Gain</h2>

                <div className={classes.faq}>
                    <h3>Is Volta SNAP a verified software for performing CSA F280-12 calculations?</h3>
                    <p>
                        Yes! Volta SNAP has been third-party verified by HVAC Designers of Canada and meets the
                        requirements of Section 8 of CSA F280-12: “Determining the required capacity of residential
                        space heating and cooling appliances.” Learn more at{" "}
                        <a rel="noopener noreferrer" target="_blank" href="https://hvacdc.ca/software/">
                            https://hvacdc.ca/software/
                        </a>
                        .
                    </p>
                </div>

                <div className={classes.faq}>
                    <h3>Can a file used for CSA F280-12 calculations be submitted to NRCan?</h3>
                    <p>
                        In general, no. F280 often requires users to alter certain parameters (basement insulation
                        configuration, for example) so that they differ from EnerGuide Rating System (ERS) requirements.
                        If you intend to use a file for both purposes, it is best practice to duplicate the file first.
                    </p>
                </div>

                <div className={classes.faq}>
                    <h3>Can I run CSA F280-12 calculations if I'm not a Registered Energy Advisor?</h3>
                    <p>
                        Yes, F280 calculations are available for any <strong>verified</strong> industry professional
                        user.
                    </p>
                </div>

                <div className={classes.faq}>
                    <h3>
                        How do I get my heat loss &amp; gain calculation training credentials reflected in the F280
                        report?
                    </h3>
                    <p>
                        Contact Volta SNAP support staff with proof of completion of a residential heat loss/heat gain
                        course. Courses are offered regularly by CACEA, HRAI, TECA, and Green Communities Canada.
                    </p>
                </div>

                <div className={classes.faq}>
                    <h3>Is the Drawing Capture Tool currently available for room-by-room data entry?</h3>
                    <p>Not yet, but it's on Volta SNAP's development roadmap!</p>
                    <p>
                        At the moment, room-by-room calculations are designed to align as closely as possible with
                        HOT2000 file inputs.
                    </p>
                </div>

                <div className={classes.faq}>
                    <h3>Where can I find Room-by-room calculation results?</h3>
                    <p>
                        Room-by-room calculation results are available in the <strong>Insights</strong> section of the
                        model.
                    </p>
                </div>

                <div className={classes.faq}>
                    <h3>
                        Adding wall insulation to my crawl space components isn't reducing the F280 heat loss value. Why
                        is that?
                    </h3>
                    <p>
                        F280 doesn't have a special case for crawl spaces, and they're handled in one of the following
                        ways, depending on the inputs provided.
                    </p>
                    <ul>
                        <li>
                            <strong>If a crawl space is not included in the heated volume:</strong> Heat loss occurs
                            through the "Floor above" the crawl space. No losses through the crawl space walls or slab
                            are considered. This is effectively like having an exposed floor exposed to ambient
                            conditions. It doesn't matter if the crawl space is closed, open, or vented; F280 takes a
                            conservative approach. Adding wall insulation will have no impact on heat loss in this case.
                        </li>
                        <li>
                            <strong>
                                If a crawl space is within the heated volume and the walls have a below-grade depth:
                            </strong>{" "}
                            the crawl space is modelled as a basement using the BASESIMP model defined by F280. Heat
                            flow across the "Floor above" the crawl space is not considered.
                        </li>
                        <li>
                            <strong>
                                If a crawl space is within the heated volume and the walls have no below-grade depth:
                            </strong>{" "}
                            The crawl space is effectively a room with a slab-on-grade floor, and losses are modelled
                            through its walls as if they're typical above-grade walls and through its slab as if it's a
                            slab-on-grade component. Heat flow across the "Floor above" the crawl space is not
                            considered.
                        </li>
                    </ul>
                    <p>
                        Users also have the option to include the crawl space in the heated volume but use a lower set
                        point (15˚C instead of 22˚C) for that space for F280 calculations. This toggle is in the{" "}
                        <strong>Systems/Temperature</strong> tab. It is up to the user's discretion to model the crawl
                        space in whichever way most accurately reflects reality.
                    </p>
                </div>
            </div>

            <div className={classes.faqSection}>
                <h2>Parametric &amp; Community Analysis Tools</h2>

                <div className={classes.faq}>
                    <h3>Will the parametric analysis tool be expanded to support building codes beyond the NBC?</h3>
                    <p>Yes, BCBC and OBC-specific metrics will be coming soon!</p>
                </div>

                <div className={classes.faq}>
                    <h3>How do credits work for Parametric Analysis?</h3>
                    <p>
                        Parametric Analysis runs are unlocked with credits in batches of <strong>2,500 runs</strong> per
                        credit. If an analysis needs more capacity than you currently have unlocked, you can spend
                        additional credits to unlock further batches of 2,500 runs.
                    </p>
                    <p>
                        Activating <strong>Insights</strong> on a model costs <strong>1 credit</strong> and permanently
                        unlocks Insights features for that model. When you create your first parametric analysis from
                        that model after Insights is activated, it includes{" "}
                        <strong>2,500 unlocked parametric runs</strong>.
                    </p>
                    <p>
                        Credits can be purchased from the app header, and organization admins can transfer credits
                        between users. If you have legacy credits from before version 1.0.0, you can still use them to
                        unlock parametric runs or transfer them to the new credit system from the Transactions page.
                    </p>
                </div>

                <div className={classes.faq}>
                    <h3>Does a Parametric Analysis produce code-compliant results?</h3>
                    <p>
                        No, all parametric analyses are run in General mode of HOT2000 to reduce computing time and
                        cost. However, when running parametric and community simulations, Volta SNAP applies
                        modifications to all models to approximate Standard Operating Conditions (SOC). While these
                        results are not identical to ERS-mode simulation results, they typically produce results within
                        0.5% of SOC.
                    </p>
                </div>
            </div>
        </div>
    </div>
);
